"use client";

import { useState } from "react";
import Image from "next/image";
import MagneticButton from "./MagneticButton";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail("");
    }
  };

  return (
    <footer role="contentinfo" className="relative" style={{ background: "linear-gradient(180deg, #fbf8f2 0%, #efe6d6 100%)" }}>
      {/* Newsletter section */}
      <div className="border-b border-[#221d17]/8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[10px] tracking-[0.4em] uppercase text-[#8a6a2c]/70 mb-4">
                Stay Connected
              </p>
              <h3
                className="font-serif text-3xl md:text-4xl tracking-tight text-[#221d17] mb-3"
                style={{ fontWeight: 300 }}
              >
                Join the Inner Circle
              </h3>
              <p className="text-sm text-[#6f6759]/75 font-light max-w-md">
                First access to limited batches, private events, and distillery secrets.
              </p>
            </div>

            <div>
              {subscribed ? (
                <p className="text-sm text-[#8a6a2c] font-light">Welcome to the circle.</p>
              ) : (
                <form onSubmit={handleSubscribe} className="flex gap-0">
                  <input
                    type="email"
                    name="email"
                    id="subscription-email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    aria-label="Email address"
                    className="flex-1 bg-white/60 border border-[#221d17]/10 border-r-0 px-5 py-4 text-xs text-[#221d17]/80 placeholder:text-[#6f6759]/40 focus:outline-none focus:border-[#c9a84c]/50 transition-colors"
                    required
                  />
                  <MagneticButton strength={0.1}>
                    <button
                      type="submit"
                      className="px-8 py-4 bg-[#c9a84c]/15 border border-[#c9a84c]/30 text-[10px] tracking-[0.2em] uppercase text-[#8a6a2c] hover:text-[#6f551f] hover:bg-[#c9a84c]/20 transition-all duration-500"
                    >
                      Subscribe
                    </button>
                  </MagneticButton>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="relative w-8 h-8">
                <Image
                  src="/images/logo-main.png"
                  alt="STYGIAN"
                  fill
                  className="object-contain"
                  sizes="32px"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-serif text-lg tracking-[0.15em] uppercase text-[#221d17]">
                  Stygian
                </span>
                <span className="text-[6px] tracking-[0.4em] uppercase text-[#8a6a2c]/70 -mt-0.5">
                  Parfums
                </span>
              </div>
            </div>
            <p className="text-xs text-[#6f6759]/70 font-light leading-relaxed max-w-xs">
              Luxury fragrances crafted from the rarest botanicals. Each scent is an invitation to embrace the extraordinary.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase text-[#8a6a2c]/70 mb-5">
              Collection
            </p>
            <div className="space-y-3">
              {["Belladonna", "Wolfsbane", "The Pair", "Gift Sets"].map((link) => (
                <a
                  key={link}
                  href="#"
                  className="block text-xs text-[#6f6759]/70 hover:text-[#8a6a2c] transition-colors duration-300"
                >
                  {link}
                </a>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase text-[#8a6a2c]/70 mb-5">
              Order Now
            </p>
            <div className="space-y-3">
              <a
                href="tel:+923205038329"
                className="flex items-center gap-2 text-xs text-[#6f6759]/70 hover:text-[#8a6a2c] transition-colors duration-300"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="opacity-60">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                +92 320 503 8329
              </a>
              <a
                href="tel:+923299641920"
                className="flex items-center gap-2 text-xs text-[#6f6759]/70 hover:text-[#8a6a2c] transition-colors duration-300"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="opacity-60">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                +92 329 964 1920
              </a>
            </div>
          </div>

          {/* Social */}
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase text-[#8a6a2c]/70 mb-5">
              Follow Us
            </p>
            <div className="flex gap-4">
              {[
                {
                  name: "YouTube",
                  href: "https://www.youtube.com/@stygianperfumes",
                  icon: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
                },
                {
                  name: "Instagram",
                  href: "https://www.instagram.com/stygian_perfume/",
                  icon: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z",
                },
                {
                  name: "TikTok",
                  href: "https://www.tiktok.com/@stygian_perfume",
                  icon: "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z",
                },
                {
                  name: "Facebook",
                  href: "https://www.facebook.com/profile.php?id=61594394020480",
                  icon: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
                },
              ].map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center border border-[#221d17]/10 text-[#6f6759]/70 hover:text-[#8a6a2c] hover:border-[#c9a84c]/40 transition-all duration-300"
                  aria-label={social.name}
                  data-cursor="pointer"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d={social.icon} />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[#221d17]/8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[9px] tracking-[0.15em] uppercase text-[#6f6759]/60">
            &copy; 2026 Stygian Parfums. All rights reserved.
          </p>
          <div className="flex gap-6">
            {["Privacy", "Terms", "Shipping"].map((link) => (
              <a
                key={link}
                href="#"
                className="text-[9px] tracking-[0.15em] uppercase text-[#6f6759]/60 hover:text-[#8a6a2c]/80 transition-colors"
              >
                {link}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}